import is from '@sindresorhus/is';
import { formatDistance } from 'date-fns';
import type { FunctionComponent } from 'react';
import Maybe from 'true-myth/maybe';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { formatDateAndTimeByLocale } from '../../date/date-formatter';

export const DateText: FunctionComponent<{ readonly value?: string }> = ({ value }) => {
    const {
        applicationState: { clock, language, languageKey },
    } = useApplicationContext();

    if (!is.nonEmptyString(value)) return <span>–</span>;

    const relativeTime = formatDistance(new Date(value), clock.getCurrentTimestamp(), {
        addSuffix: true,
        locale: language.date.locale,
    });
    const exactDateAndTime = formatDateAndTimeByLocale({
        dateStringToFormat: Maybe.just(value),
        locale: languageKey,
    }).unwrapOr('');

    if (!is.nonEmptyString(exactDateAndTime)) return <span>{relativeTime}</span>;

    return (
        <Tooltip>
            <Tooltip.Trigger asChild={true}>
                {/* biome-ignore lint/a11y/noNoninteractiveTabindex: the tooltip with the exact date opens on focus, so keyboard users need to be able to focus the trigger. */}
                <span tabIndex={0}>{relativeTime}</span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top">{exactDateAndTime}</Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};
